import type { Annotation } from "@rapid/types/annotation";
import { Prediction } from "@rapid/utils/api/inference";
import { DEFAULT_CONFIDENCE_THRESHOLD } from "@rapid/utils/predictions";
import { RapidStateCreator } from "../types";
import { MediaItem } from "@rapid/types/media";

export interface AnnotationsForFile {
    [frameId: string]: Annotation[];
}

export interface AnnotationsForFileAndFrameId {
    [fileUuid: string]: AnnotationsForFile;
}

export interface PredictionsForFile {
    [frameSegment: number]: Prediction[];
}

export interface PredictionsForFileAndFrameSegment {
    [fileUuid: string]: PredictionsForFile;
}

// Types for the async inference queue
export enum InferenceStatus {
    QUEUED = "queued",
    INFERRING = "inferring",
    COMPLETED = "completed",
    ERROR = "error",
    ABORTED = "aborted"
}

// Abort data received from the backend when inference is aborted
export interface InferenceAbortData {
    reason: string;
    errorCounts: Record<string, number>;
    totalErrorCount: number;
    threshold: number;
    processedBeforeAbort: number;
    totalItems: number;
}

export interface InferenceQueueItem {
    id: string; // mediaItem.id
    status: InferenceStatus;
    promise: Promise<void>;
    abortController: AbortController;
    progress: number; // 0-100 percentage
    error?: Error;
    abortData?: InferenceAbortData;
}

export interface PromptLabel {
    name: string;
    confidenceThreshold?: number;
    customColor?: string; // Optional custom color, if not set, will use generated color
    hasDiffs?: boolean; // True when user has manually edited/deleted/drawn annotations for this label
}
export interface PromptLabels extends Map<string, PromptLabel> {}

/**
 * Creates a PromptLabel with preserved settings from previous label and optional max confidence.
 * This helper ensures consistent label creation logic across updatePromptLabels and syncPromptLabels.
 */
function createPromptLabelWithPreservedSettings(
    labelName: string,
    previousLabel: PromptLabel | undefined,
    maxConfidence?: number
): PromptLabel {
    return {
        name: previousLabel?.name || labelName,
        confidenceThreshold:
            previousLabel?.confidenceThreshold ||
            (maxConfidence ? Math.min(maxConfidence, DEFAULT_CONFIDENCE_THRESHOLD) : undefined),
        customColor: previousLabel?.customColor,
        hasDiffs: previousLabel?.hasDiffs
    };
}

export interface ModelIntentSliceState {
    predictionsForFileAndFrameSegment: PredictionsForFileAndFrameSegment;
    annotationsForFileAndFrameId: AnnotationsForFileAndFrameId;
    inferenceQueue: Map<string, InferenceQueueItem>;
    promptLabels: PromptLabels; // derived state. set of unique labels from annotations, predictions and text prompts

    // Getters - computed values for easy access
    getHasAnnotations: () => boolean;
    getHasPredictions: () => boolean;

    // Inference queue getters
    getInferenceQueueItemById: (id: string) => InferenceQueueItem | undefined;

    // Actions - Media Item management
    syncPredictionsAndAnnotationWithMediaItems: (mediaItems: MediaItem[]) => void;

    // Actions - Annotations management
    setAnnotationsForFileAndFrameId: (
        updater:
            | AnnotationsForFileAndFrameId
            | ((prev: AnnotationsForFileAndFrameId) => AnnotationsForFileAndFrameId)
    ) => void;
    clearAnnotationsForFile: (fileId: string) => void;
    clearAllAnnotations: () => void;

    // Actions - Predictions management
    setPredictionsForFileAndFrameSegment: (
        updater:
            | PredictionsForFileAndFrameSegment
            | ((prev: PredictionsForFileAndFrameSegment) => PredictionsForFileAndFrameSegment)
    ) => void;
    clearPredictionsForFile: (fileId: string) => void;
    clearPredictionsForFileAndFrameSegment: (fileId: string, frameSegment: number) => void;
    clearAllPredictions: () => void;

    // Actions - Inference queue management
    addToInferenceQueue: (queueItem: InferenceQueueItem) => void;
    updateInferenceQueueItemStatus: (
        id: string,
        status: InferenceStatus,
        error?: Error,
        abortData?: InferenceAbortData
    ) => void;
    updateInferenceQueueItemProgress: (id: string, progress: number) => void;
    clearInferenceQueue: () => void;
    removeFromInferenceQueue: (id: string) => void;

    // Prompt labels management
    setPromptLabels: (labels: PromptLabels) => void;
    updatePromptLabels: (labels: PromptLabel[], labelsToDelete?: string[]) => void;
    setPromptLabelsGlobalConfidenceThreshold: (confidenceThreshold: number) => void;
    syncPromptLabels: (newLabels?: string[]) => void; // method to call to sync prompt labels with changes to annotations, predictions and text prompts
    markLabelAsEdited: (labelName: string) => void; // marks a label as having user edits (hasDiffs = true)

    // User annotations snapshot — preserves user-drawn annotations before inference overwrites them.
    // Used by the Review step's "My Annotations" tab to show original user work.
    userAnnotationsSnapshot: AnnotationsForFileAndFrameId;
    snapshotUserAnnotations: () => void;
    clearUserAnnotationsSnapshot: () => void;

    // When viewing a video's annotated frames as individual images ("flattened" view),
    // currentMedia.file is set to the frame (not the video). This stores the original
    // video file ID so annotation lookups can still find annotations keyed by video ID.
    flattenedVideoSourceId: string | null;
    setFlattenedVideoSourceId: (id: string | null) => void;
}

export const createModelIntentSlice: RapidStateCreator<ModelIntentSliceState> = (set, get) => {
    return {
        // Initial state
        predictionsForFileAndFrameSegment: {},
        annotationsForFileAndFrameId: {},
        inferenceQueue: new Map(),
        promptLabels: new Map(),

        getHasAnnotations: () => {
            const allAnnotations = get().annotationsForFileAndFrameId;

            for (const fileUuid in allAnnotations) {
                const framesData = allAnnotations[fileUuid];
                for (const frameId in framesData) {
                    if (framesData[frameId] && framesData[frameId].length > 0) {
                        return true;
                    }
                }
            }
            return false;
        },

        getHasPredictions: () => {
            const allPredictions = get().predictionsForFileAndFrameSegment;

            for (const fileUuid in allPredictions) {
                const framesData = allPredictions[fileUuid];
                for (const frameSegment in framesData) {
                    if (framesData[frameSegment] && framesData[frameSegment].length > 0) {
                        return true;
                    }
                }
            }
            return false;
        },

        getInferenceQueueItemById: (id: string) => {
            return get().inferenceQueue.get(id);
        },

        // media item management
        syncPredictionsAndAnnotationWithMediaItems: (mediaItems: MediaItem[]) => {
            const fileIds = new Set(mediaItems.map((mediaItem) => mediaItem.id));

            set((state: any) => {
                // Create shallow copies before mutation
                const allPredictions = { ...state.predictionsForFileAndFrameSegment };
                Object.keys(allPredictions).forEach((fileId) => {
                    if (!fileIds.has(fileId)) {
                        delete allPredictions[fileId];
                    }
                });
                const allAnnotations = { ...state.annotationsForFileAndFrameId };
                Object.keys(allAnnotations).forEach((fileId) => {
                    if (!fileIds.has(fileId)) {
                        delete allAnnotations[fileId];
                    }
                });
                return {
                    predictionsForFileAndFrameSegment: allPredictions,
                    annotationsForFileAndFrameId: allAnnotations
                };
            });
        },

        // Annotations management
        setAnnotationsForFileAndFrameId: (updater) => {
            set((state: any) => {
                const newState =
                    typeof updater === "function"
                        ? updater(state.annotationsForFileAndFrameId)
                        : updater;

                return { annotationsForFileAndFrameId: newState };
            });
        },

        clearAnnotationsForFile: (fileId: string) => {
            set((state: any) => {
                const allAnnotations = { ...state.annotationsForFileAndFrameId };
                delete allAnnotations[fileId];
                return { annotationsForFileAndFrameId: allAnnotations };
            });
        },

        clearAllAnnotations: () => {
            set((_state: any) => ({ annotationsForFileAndFrameId: {} }));
        },

        // Predictions management
        setPredictionsForFileAndFrameSegment: (updater) => {
            set((state: any) => {
                const newState =
                    typeof updater === "function"
                        ? updater(state.predictionsForFileAndFrameSegment)
                        : updater;

                return { predictionsForFileAndFrameSegment: newState };
            });
        },

        clearPredictionsForFile: (fileId: string) => {
            set((state: any) => {
                const allPredictions = { ...state.predictionsForFileAndFrameSegment };
                delete allPredictions[fileId];
                return { predictionsForFileAndFrameSegment: allPredictions };
            });
        },

        clearPredictionsForFileAndFrameSegment: (fileId: string, frameSegment: number) => {
            set((state: any) => {
                const allPredictions = { ...state.predictionsForFileAndFrameSegment };
                if (allPredictions[fileId]) {
                    // Also create shallow copy of nested object before mutation
                    allPredictions[fileId] = { ...allPredictions[fileId] };
                    delete allPredictions[fileId][frameSegment];
                }
                return { predictionsForFileAndFrameSegment: allPredictions };
            });
        },

        clearAllPredictions: () => {
            set((_state: any) => ({ predictionsForFileAndFrameSegment: {} }));
        },

        // Inference queue management
        addToInferenceQueue: (queueItem: InferenceQueueItem) => {
            set((state: any) => {
                const newQueue = new Map<string, InferenceQueueItem>(state.inferenceQueue);
                newQueue.set(queueItem.id, queueItem);
                return { inferenceQueue: newQueue };
            });
        },

        updateInferenceQueueItemStatus: (
            id: string,
            status: InferenceStatus,
            error?: Error,
            abortData?: InferenceAbortData
        ) => {
            set((state: any) => {
                const newQueue = new Map<string, InferenceQueueItem>(state.inferenceQueue);
                const item = newQueue.get(id);
                if (item) {
                    newQueue.set(id, { ...item, status, error, abortData });
                }
                return { inferenceQueue: newQueue };
            });
        },

        updateInferenceQueueItemProgress: (id: string, progress: number) => {
            set((state: any) => {
                const newQueue = new Map<string, InferenceQueueItem>(state.inferenceQueue);
                const item = newQueue.get(id);
                if (item) {
                    newQueue.set(id, { ...item, progress });
                }
                return { inferenceQueue: newQueue };
            });
        },

        clearInferenceQueue: () => {
            const inferenceQueue = get().inferenceQueue;
            // Abort all controllers before clearing
            inferenceQueue.forEach((item: InferenceQueueItem) => {
                if (item.abortController && !item.abortController.signal.aborted) {
                    item.abortController.abort();
                }
            });
            // Create new empty Map instead of mutating
            set((_state: any) => ({ inferenceQueue: new Map() }));
        },

        removeFromInferenceQueue: (id: string) => {
            set((state: any) => {
                const newQueue = new Map<string, InferenceQueueItem>(state.inferenceQueue);
                if (newQueue.has(id)) {
                    const item = newQueue.get(id);
                    if (item && item.abortController && !item.abortController.signal.aborted) {
                        item.abortController.abort();
                    }
                    newQueue.delete(id);
                }
                return { inferenceQueue: newQueue };
            });
        },

        // Prompt labels management
        setPromptLabels: (labels: PromptLabels) => {
            set((_state: any) => ({ promptLabels: labels }));
        },

        updatePromptLabels: (labels: PromptLabel[], labelsToDelete?: string[]) => {
            set((state) => {
                const allPromptLabels = new Map(state.promptLabels);

                // Delete specified labels first
                if (labelsToDelete) {
                    labelsToDelete.forEach((labelName) => {
                        allPromptLabels.delete(labelName);
                    });
                }

                // Then update/add the provided labels
                labels.forEach((label) => {
                    allPromptLabels.set(label.name, label);
                });

                return { promptLabels: allPromptLabels };
            });
        },

        setPromptLabelsGlobalConfidenceThreshold: (confidenceThreshold: number) => {
            set(() => ({
                promptLabels: new Map(
                    get()
                        .promptLabels.entries()
                        .map(([key, value]: [string, PromptLabel]) => [
                            key,
                            { ...value, confidenceThreshold }
                        ])
                )
            }));
        },

        syncPromptLabels: (newLabels?: string[]) => {
            const allAnnotations = get().annotationsForFileAndFrameId;
            const allPredictions = get().predictionsForFileAndFrameSegment;
            const allPromptLabels = get().promptLabels;

            const maxConfidence = new Map<string, number>();
            const uniqueLabels = new Set<string>(newLabels || []);
            const isInclusionMode = newLabels !== undefined;
            const inclusionSet = isInclusionMode ? new Set(newLabels) : null;

            Object.keys(allAnnotations).forEach((fileId) => {
                Object.keys(allAnnotations[fileId]).forEach((frameId) => {
                    allAnnotations[fileId][frameId].forEach((annotation: Annotation) => {
                        if (!isInclusionMode || inclusionSet!.has(annotation.label)) {
                            uniqueLabels.add(annotation.label);
                            maxConfidence.set(
                                annotation.label,
                                Math.max(
                                    maxConfidence.get(annotation.label) || 0,
                                    annotation.confidence || 0
                                )
                            );
                        }
                    });
                });
            });

            Object.keys(allPredictions).forEach((fileId) => {
                Object.keys(allPredictions[fileId]).forEach((frameSegment) => {
                    allPredictions[fileId][frameSegment].forEach((prediction: Prediction) => {
                        if (!isInclusionMode || inclusionSet!.has(prediction.class)) {
                            uniqueLabels.add(prediction.class);
                            maxConfidence.set(
                                prediction.class,
                                Math.max(
                                    maxConfidence.get(prediction.class) || 0,
                                    prediction.confidence || 0
                                )
                            );
                        }
                    });
                });
            });

            const newPromptLabels = new Map<string, PromptLabel>();

            uniqueLabels.forEach((label) => {
                const labelSettingPrev = allPromptLabels.get(label);
                const labelSettings = createPromptLabelWithPreservedSettings(
                    label,
                    labelSettingPrev,
                    maxConfidence.get(label)
                );
                newPromptLabels.set(label, labelSettings);
            });

            if (!newLabels) {
                allPromptLabels.forEach((labelSettings, label) => {
                    if (!uniqueLabels.has(label)) {
                        newPromptLabels.set(label, labelSettings);
                    }
                });
            }

            set((_state: any) => ({ promptLabels: newPromptLabels }));
        },

        // User annotations snapshot
        userAnnotationsSnapshot: {},

        snapshotUserAnnotations: () => {
            set((state: any) => {
                // Don't overwrite an existing snapshot — it was taken closer to the
                // original user annotations and is therefore more accurate.
                if (Object.keys(state.userAnnotationsSnapshot).length > 0) {
                    return state;
                }

                const current: AnnotationsForFileAndFrameId = state.annotationsForFileAndFrameId;
                const promptLabels: PromptLabels = state.promptLabels;
                const snapshot: AnnotationsForFileAndFrameId = {};

                for (const fileId of Object.keys(current)) {
                    snapshot[fileId] = {};
                    for (const frameId of Object.keys(current[fileId])) {
                        const annotations = current[fileId][frameId] || [];
                        snapshot[fileId][frameId] = annotations
                            .filter((a: Annotation) => {
                                if (a.draft) return false;
                                // Manually drawn annotations (no confidence) are always kept
                                if (a.confidence == null) return true;
                                // Prediction-converted annotations: keep only those above
                                // the label's confidence threshold
                                const threshold =
                                    promptLabels.get(a.label)?.confidenceThreshold ??
                                    DEFAULT_CONFIDENCE_THRESHOLD;
                                return a.confidence >= threshold;
                            })
                            .map((a: Annotation) => ({ ...a }));
                    }
                }

                return { userAnnotationsSnapshot: snapshot };
            });
        },

        clearUserAnnotationsSnapshot: () => {
            set((_state: any) => ({ userAnnotationsSnapshot: {} }));
        },

        flattenedVideoSourceId: null,

        setFlattenedVideoSourceId: (id: string | null) => {
            set({ flattenedVideoSourceId: id });
        },

        markLabelAsEdited: (labelName: string) => {
            set((state) => {
                const allPromptLabels = new Map(state.promptLabels);
                const existingLabel = allPromptLabels.get(labelName);
                const confidenceThreshold =
                    existingLabel?.confidenceThreshold ?? DEFAULT_CONFIDENCE_THRESHOLD;

                // Update the label to mark it as edited
                if (existingLabel) {
                    allPromptLabels.set(labelName, { ...existingLabel, hasDiffs: true });
                } else {
                    // Create a new label if it doesn't exist
                    allPromptLabels.set(labelName, { name: labelName, hasDiffs: true });
                }

                // Snapshot: filter out annotations below confidence threshold for this label
                // This ensures that when hasDiffs is true, only the visible annotations remain
                const allAnnotations = state.annotationsForFileAndFrameId;
                const newAnnotations: AnnotationsForFileAndFrameId = {};

                Object.keys(allAnnotations).forEach((fileId) => {
                    newAnnotations[fileId] = {};
                    Object.keys(allAnnotations[fileId]).forEach((frameId) => {
                        newAnnotations[fileId][frameId] = allAnnotations[fileId][frameId].filter(
                            (annotation) => {
                                // Keep annotations that are not for this label
                                if (annotation.label !== labelName) {
                                    return true;
                                }
                                // For this label, only keep annotations that pass confidence threshold
                                // or have no confidence (manually drawn)
                                return (
                                    !annotation.confidence ||
                                    annotation.confidence >= confidenceThreshold
                                );
                            }
                        );
                    });
                });

                return {
                    promptLabels: allPromptLabels,
                    annotationsForFileAndFrameId: newAnnotations
                };
            });
        }
    };
};
